import { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { QrCode, Download, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function QRCheckInDisplay() {
  const [expanded, setExpanded] = useState(false);
  const qrRef = useRef(null);
  const checkinUrl = `${BACKEND_URL}/checkin`;

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;

    const logo = new Image();
    logo.crossOrigin = 'anonymous';
    logo.onload = () => {
      const downloadCanvas = document.createElement('canvas');
      const ctx = downloadCanvas.getContext('2d');
      const padding = 60;
      const qrSize = 400;
      const logoSize = 80;
      const headerHeight = logoSize + 80;
      const footerHeight = 60;
      downloadCanvas.width = qrSize + padding * 2;
      downloadCanvas.height = headerHeight + qrSize + footerHeight;

      // Background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height);

      // Logo
      const logoX = (downloadCanvas.width - logoSize) / 2;
      ctx.drawImage(logo, logoX, 15, logoSize, logoSize);

      // Title
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('R&R ELITE FITNESS', downloadCanvas.width / 2, logoSize + 35);

      // Subtitle
      ctx.font = '16px Arial';
      ctx.fillStyle = '#666666';
      ctx.fillText('Scan to Check In', downloadCanvas.width / 2, logoSize + 60);

      // QR Code
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCanvas.width = qrSize;
      tempCanvas.height = qrSize;
      tempCtx.drawImage(canvas, 0, 0, qrSize, qrSize);
      ctx.drawImage(tempCanvas, padding, headerHeight, qrSize, qrSize);

      // Footer
      ctx.font = '13px Arial';
      ctx.fillStyle = '#999999';
      ctx.fillText('Open your camera and scan this code when you arrive', downloadCanvas.width / 2, headerHeight + qrSize + 25);
      ctx.font = '10px Arial';
      ctx.fillText(checkinUrl, downloadCanvas.width / 2, headerHeight + qrSize + 45);

      // Download
      const link = document.createElement('a');
      link.download = 'RR-Elite-QR-Checkin.png';
      link.href = downloadCanvas.toDataURL('image/png');
      link.click();
    };
    logo.onerror = () => {
      // Fallback without logo
      const downloadCanvas = document.createElement('canvas');
      const ctx = downloadCanvas.getContext('2d');
      const padding = 60;
      const qrSize = 400;
      downloadCanvas.width = qrSize + padding * 2;
      downloadCanvas.height = qrSize + padding * 2 + 140;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height);
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('R&R ELITE FITNESS', downloadCanvas.width / 2, 45);
      ctx.font = '18px Arial';
      ctx.fillStyle = '#666666';
      ctx.fillText('Scan to Check In', downloadCanvas.width / 2, 75);
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCanvas.width = qrSize;
      tempCanvas.height = qrSize;
      tempCtx.drawImage(canvas, 0, 0, qrSize, qrSize);
      ctx.drawImage(tempCanvas, padding, 95, qrSize, qrSize);
      ctx.font = '14px Arial';
      ctx.fillStyle = '#999999';
      ctx.fillText('Open your camera and scan this code', downloadCanvas.width / 2, qrSize + 120);
      const link = document.createElement('a');
      link.download = 'RR-Elite-QR-Checkin.png';
      link.href = downloadCanvas.toDataURL('image/png');
      link.click();
    };
    logo.src = 'https://customer-assets.emergentagent.com/job_wod-trainer/artifacts/rhui1jxy_R%26R%20ELITE_20250812_172035_0000.png';
  };

  return (
    <Card className="bg-[#18181B] border-[#27272A] overflow-hidden" data-testid="qr-checkin-display">
      {/* Collapsible Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        data-testid="qr-toggle"
        className="w-full flex items-center justify-between p-4 hover:bg-[#1F1F23] transition-colors"
      >
        <div className="flex items-center gap-3">
          <QrCode className="w-5 h-5 text-[#CCFF00]" />
          <span className="text-base font-heading font-bold uppercase text-[#FAFAFA]">QR Check-In</span>
          <span className="text-xs text-[#52525B]">Print and display at your gym</span>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-[#A1A1AA]" /> : <ChevronDown className="w-5 h-5 text-[#A1A1AA]" />}
      </button>

      {/* Expandable Content */}
      {expanded && (
        <div className="px-4 pb-5 border-t border-[#27272A]">
          <p className="text-sm text-[#A1A1AA] mt-4 mb-4">
            Athletes scan this QR code to automatically check in to the current class.
          </p>

          <div className="flex flex-col items-center gap-4" ref={qrRef}>
            <div className="bg-white p-3 rounded-lg" data-testid="qr-code">
              <QRCodeCanvas
                value={checkinUrl}
                size={180}
                level="H"
                includeMargin={false}
              />
            </div>
            <p className="text-xs text-[#52525B] break-all text-center max-w-xs">{checkinUrl}</p>
            <Button
              onClick={downloadQR}
              data-testid="download-qr-button"
              className="bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600] rounded-sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Download QR Code
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
